import React, { FormEvent, useCallback, useRef, useState } from 'react';

import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import InputCruiseLineSelect from '~/components/Cruises/components/Selects/InputCruiseLineSelect';
import { STATUS } from '~/components/Cruises/constants';
import { useCruiseOffers } from '~/components/Cruises/context/useCruiseOffers';

import { formatDateISO } from '~/services/TimeService';
import { CruiseLine } from '~/services/cruises/CruiseLineService';

function CruiseSearch() {
  const [date, setDate] = useState<string>(null);

  const formRef = useRef(null);
  const { filter, setFilter } = useCruiseOffers();
  const [cruiseLine, setCruiseLine] = useState<CruiseLine>(null);

  const handleCruiseLineChange = useCallback(
    (cruiseLine: CruiseLine) => {
      setCruiseLine(cruiseLine);
    },
    [setCruiseLine],
  );

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      const form = formRef.current;
      event.preventDefault();

      setFilter({
        id: form['offerId']?.value,
        name: form['offerName']?.value,
        status: form['offerStatus']?.value,
        shipName: form['shipName']?.value,
        cruiseLine: cruiseLine?.name,
        rateCode: form['rateCode']?.value,
        marketName: form['marketName']?.value,
        departureDate: date && formatDateISO(new Date(date)),
        departureExternalId: form['departureExternalId']?.value,
      });
    },
    [date, cruiseLine, setFilter],
  );

  return (
    <Box
      mt={5}
      sx={{
        borderRadius: '8px',
        padding: '32px 24px',
        backgroundColor: '#fafafa',
        border: '1px solid #f2f2f2',
      }}
    >
      <form ref={formRef} onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid size={4}>
            <TextField fullWidth name="offerId" label="Offer ID" />
          </Grid>

          <Grid size={4}>
            <TextField fullWidth name="offerName" label="Offer Name" />
          </Grid>

          <Grid size={4}>
            <FormControl fullWidth>
              <InputLabel>Offer Status</InputLabel>
              <Select fullWidth defaultValue={filter.status} label="Offer Status" name="offerStatus">
                {STATUS.map((item, i) => (
                  <MenuItem key={i} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={4}>
            <TextField fullWidth name="departureExternalId" label="Sailing or Tour ID" type="number" />
          </Grid>

          <Grid size={4}>
            <FormControl fullWidth>
              <InputCruiseLineSelect label="Cruise line" value={filter.cruiseLine} onChange={handleCruiseLineChange} />
            </FormControl>
          </Grid>

          <Grid size={4}>
            <TextField fullWidth name="rateCode" label="Rate Code" />
          </Grid>

          <Grid size={4}>
            <TextField fullWidth name="shipName" label="Ship Name" />
          </Grid>

          <Grid size={3}>
            <TextField fullWidth name="marketName" label="Market Name" />
          </Grid>

          <Grid size={3}>
            <DateTimeWidget
              showDateOnly
              value={date}
              onChange={setDate}
              disablePast={false}
              format="DD/MM/YYYY"
              label="Departure Date"
            />
          </Grid>

          <Grid size={2}>
            <Button fullWidth size="large" variant="contained" type="submit">
              Search Content
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default CruiseSearch;
